/**
 * Visualizador de transcrição com busca e navegação por timestamp
 * Integração com anotações, destaques e favoritos
 */

import React, { useState, useMemo, useRef, useEffect } from 'react'
import {
  Box,
  TextField,
  InputAdornment,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Collapse,
  Badge,
  Slider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
  Stack
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import PersonIcon from '@mui/icons-material/Person'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import NoteAddIcon from '@mui/icons-material/NoteAdd'
import BookmarkIcon from '@mui/icons-material/Bookmark'
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder'
import HighlightIcon from '@mui/icons-material/Highlight'
import EditNoteIcon from '@mui/icons-material/EditNote'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import DeleteIcon from '@mui/icons-material/Delete'
import FilterListIcon from '@mui/icons-material/FilterList'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import TuneIcon from '@mui/icons-material/Tune'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { Transcription, TranscriptSegment, SearchResult, UserAnnotations, Annotation, SegmentHighlight, Bookmark, AnnotationColor, AnnotationCategory } from '../../types/transcription'
import { searchTranscript, formatTimestamp, highlightLegalTerms } from '../../services/transcriptionAnalysis'
import { CATEGORY_COLORS } from '../../constants/legalTerms'
import { SpeakerMapping } from '../../services/exportService'
import SpeakerEditor from './SpeakerEditor'

// Tipos para filtros
type AnnotationFilterType = 'all' | 'bookmarks' | 'notes' | 'highlights' | 'any-annotation'
type ConfidenceFilterType = 'all' | 'low' | 'medium' | 'high'

// Cores para anotações
const ANNOTATION_COLORS: Record<AnnotationColor, { bg: string; border: string; label: string }> = {
  yellow: { bg: '#fff9c4', border: '#fbc02d', label: 'Amarelo' },
  green: { bg: '#c8e6c9', border: '#388e3c', label: 'Verde' },
  blue: { bg: '#bbdefb', border: '#1976d2', label: 'Azul' },
  red: { bg: '#ffcdd2', border: '#d32f2f', label: 'Vermelho' },
  purple: { bg: '#e1bee7', border: '#7b1fa2', label: 'Roxo' }
}

// Categorias de anotação
const ANNOTATION_CATEGORIES: Record<AnnotationCategory, { icon: string; label: string }> = {
  note: { icon: '📝', label: 'Nota' },
  question: { icon: '❓', label: 'Questão' },
  important: { icon: '⚠️', label: 'Importante' },
  todo: { icon: '✅', label: 'A Fazer' },
  reference: { icon: '🔗', label: 'Referência' }
}

type Props = {
  transcription: Transcription
  onTimestampClick?: (seconds: number) => void
  highlightTerms?: boolean
  speakerMapping?: SpeakerMapping
  onSpeakerMappingChange?: (mapping: SpeakerMapping) => void
  scrollToSegmentIndex?: number | null
  // Anotações
  annotations?: UserAnnotations
  onAnnotationsChange?: (annotations: UserAnnotations) => void
}

export default function TranscriptViewer({ 
  transcription, 
  onTimestampClick, 
  highlightTerms = true,
  speakerMapping = {},
  onSpeakerMappingChange,
  scrollToSegmentIndex,
  annotations,
  onAnnotationsChange
}: Props) {
  const [searchQuery, setSearchQuery] = useState('')
  const [currentResultIndex, setCurrentResultIndex] = useState(0)
  const [expandedSegments, setExpandedSegments] = useState<Set<number>>(new Set())
  const segmentRefs = useRef<(HTMLDivElement | null)[]>([])
  
  // Estados para menu de contexto e diálogos de anotação
  const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number; segmentIndex: number } | null>(null)
  const [annotationDialog, setAnnotationDialog] = useState<{ open: boolean; segmentIndex: number; editingId?: string } | null>(null)
  const [annotationText, setAnnotationText] = useState('')
  const [annotationColor, setAnnotationColor] = useState<AnnotationColor>('yellow')
  const [annotationCategory, setAnnotationCategory] = useState<AnnotationCategory>('note')
  
  // Estados de filtros avançados
  const [filtersExpanded, setFiltersExpanded] = useState(false)
  const [annotationFilter, setAnnotationFilter] = useState<AnnotationFilterType>('all')
  const [selectedSpeakers, setSelectedSpeakers] = useState<string[]>([])
  const [confidenceFilter, setConfidenceFilter] = useState<ConfidenceFilterType>('all')
  const [highlightColorFilter, setHighlightColorFilter] = useState<AnnotationColor | 'all'>('all')
  
  // Calcular duração total da transcrição em segundos
  const totalDuration = useMemo(() => {
    if (transcription.transcript.length === 0) return 0
    const lastSegment = transcription.transcript[transcription.transcript.length - 1]
    return parseFloat(lastSegment.end_time) || parseFloat(lastSegment.start_time) + 10
  }, [transcription])
  
  // Estado do slider de tempo [min, max] em segundos
  const [timeRange, setTimeRange] = useState<[number, number]>([0, totalDuration])
  const [isTimeFilterActive, setIsTimeFilterActive] = useState(false)
  
  // Atualizar timeRange quando totalDuration muda
  useEffect(() => {
    if (!isTimeFilterActive) {
      setTimeRange([0, totalDuration])
    }
  }, [totalDuration, isTimeFilterActive])
  
  // Formatar segundos para exibição
  const formatSeconds = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Função para obter o nome do speaker (mapeado ou original)
  const getSpeakerName = (speaker: string) => speakerMapping[speaker] || speaker

  const searchResults = useMemo(() => {
    return searchTranscript(transcription, searchQuery)
  }, [transcription, searchQuery])

  const resultSegmentIndices = useMemo(() => {
    return [...new Set(searchResults.map(r => r.segmentIndex))]
  }, [searchResults])
  
  // Helpers para verificar anotações de um segmento (por índice)
  const getSegmentAnnotationsByIndex = (segmentIndex: number) => {
    if (!annotations) return { notes: [], highlights: [], bookmarks: [] }
    return {
      notes: annotations.items.filter(a => a.segmentIndex === segmentIndex),
      highlights: annotations.highlights.filter(h => h.segmentIndex === segmentIndex),
      bookmarks: annotations.bookmarks.filter(b => b.segmentIndex === segmentIndex)
    }
  }
  
  // Verificar se algum filtro avançado está ativo
  const hasActiveFilters = useMemo(() => {
    return isTimeFilterActive || 
           annotationFilter !== 'all' || 
           selectedSpeakers.length > 0 || 
           confidenceFilter !== 'all' ||
           highlightColorFilter !== 'all' ||
           searchQuery.trim() !== ''
  }, [isTimeFilterActive, annotationFilter, selectedSpeakers, confidenceFilter, highlightColorFilter, searchQuery])
  
  // Contar filtros ativos para o badge
  const activeFilterCount = useMemo(() => {
    let count = 0
    if (isTimeFilterActive) count++
    if (annotationFilter !== 'all') count++
    if (selectedSpeakers.length > 0) count++
    if (confidenceFilter !== 'all') count++
    if (highlightColorFilter !== 'all') count++
    return count
  }, [isTimeFilterActive, annotationFilter, selectedSpeakers, confidenceFilter, highlightColorFilter])
  
  // Filtrar segmentos com todos os critérios
  const filteredTranscript = useMemo(() => {
    return transcription.transcript.filter((segment, index) => {
      const originalIndex = index
      
      // Filtro de tempo
      if (isTimeFilterActive) {
        const startTime = parseFloat(segment.start_time)
        if (startTime < timeRange[0] || startTime > timeRange[1]) return false
      }
      
      // Filtro de speaker
      if (selectedSpeakers.length > 0 && segment.speaker) {
        if (!selectedSpeakers.includes(segment.speaker)) return false
      }
      
      // Filtro de confiança
      if (confidenceFilter !== 'all') {
        const confidence = segment.confidence || 1
        if (confidenceFilter === 'low' && confidence >= 0.7) return false
        if (confidenceFilter === 'medium' && (confidence < 0.7 || confidence >= 0.9)) return false
        if (confidenceFilter === 'high' && confidence < 0.9) return false
      }
      
      // Filtro de anotações
      if (annotationFilter !== 'all' && annotations) {
        const segAnn = getSegmentAnnotationsByIndex(originalIndex)
        const hasBookmarks = segAnn.bookmarks.length > 0
        const hasNotes = segAnn.notes.length > 0
        const hasHighlights = segAnn.highlights.length > 0
        
        if (annotationFilter === 'bookmarks' && !hasBookmarks) return false
        if (annotationFilter === 'notes' && !hasNotes) return false
        if (annotationFilter === 'highlights' && !hasHighlights) return false
        if (annotationFilter === 'any-annotation' && !hasBookmarks && !hasNotes && !hasHighlights) return false
      }
      
      // Filtro de cor de destaque
      if (highlightColorFilter !== 'all' && annotations) {
        const segAnn = getSegmentAnnotationsByIndex(originalIndex)
        const hasColorHighlight = segAnn.highlights.some(h => h.color === highlightColorFilter)
        if (!hasColorHighlight) return false
      }
      
      return true
    })
  }, [transcription.transcript, timeRange, isTimeFilterActive, selectedSpeakers, confidenceFilter, annotationFilter, highlightColorFilter, annotations])
  
  // Limpar todos os filtros
  const handleClearAllFilters = () => {
    setTimeRange([0, totalDuration])
    setIsTimeFilterActive(false)
    setAnnotationFilter('all')
    setSelectedSpeakers([])
    setConfidenceFilter('all')
    setHighlightColorFilter('all')
    setSearchQuery('')
  }

  // Scroll para o resultado atual
  useEffect(() => {
    if (resultSegmentIndices.length > 0 && currentResultIndex < resultSegmentIndices.length) {
      const segmentIndex = resultSegmentIndices[currentResultIndex]
      const ref = segmentRefs.current[segmentIndex]
      if (ref) {
        ref.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  }, [currentResultIndex, resultSegmentIndices])

  // Alias para compatibilidade (usa a mesma função)
  const getSegmentAnnotations = getSegmentAnnotationsByIndex

  const hasAnnotations = (segmentIndex: number) => {
    const { notes, highlights, bookmarks } = getSegmentAnnotations(segmentIndex)
    return notes.length > 0 || highlights.length > 0 || bookmarks.length > 0
  }

  // Abrir menu de contexto
  const handleContextMenu = (event: React.MouseEvent, segmentIndex: number) => {
    event.preventDefault()
    setContextMenu({ mouseX: event.clientX, mouseY: event.clientY, segmentIndex })
  }

  const handleCloseContextMenu = () => {
    setContextMenu(null)
  }

  // Adicionar/editar anotação
  const handleOpenAnnotationDialog = (segmentIndex: number, editingId?: string) => {
    if (editingId && annotations) {
      const existing = annotations.items.find(a => a.id === editingId)
      if (existing) {
        setAnnotationText(existing.text)
        setAnnotationColor(existing.color)
        setAnnotationCategory(existing.category)
      }
    } else {
      setAnnotationText('')
      setAnnotationColor('yellow')
      setAnnotationCategory('note')
    }
    setAnnotationDialog({ open: true, segmentIndex, editingId })
    handleCloseContextMenu()
  }

  const handleCloseAnnotationDialog = () => {
    setAnnotationDialog(null)
    setAnnotationText('')
    setAnnotationColor('yellow')
    setAnnotationCategory('note')
  }

  const handleSaveAnnotation = () => {
    if (!annotationDialog || !onAnnotationsChange || !annotationText.trim()) return
    
    const segment = transcription.transcript[annotationDialog.segmentIndex]
    const now = Date.now()
    
    const currentAnnotations = annotations || { items: [], highlights: [], bookmarks: [] }
    
    if (annotationDialog.editingId) {
      // Editando existente
      const updatedItems = currentAnnotations.items.map(a => 
        a.id === annotationDialog.editingId 
          ? { ...a, text: annotationText, color: annotationColor, category: annotationCategory, updatedAt: now }
          : a
      )
      onAnnotationsChange({ ...currentAnnotations, items: updatedItems })
    } else {
      // Nova anotação
      const newAnnotation: Annotation = {
        id: `ann_${now}_${Math.random().toString(36).substr(2, 9)}`,
        segmentIndex: annotationDialog.segmentIndex,
        startTime: segment.start_time,
        text: annotationText,
        segmentText: segment.text.substring(0, 100),
        color: annotationColor,
        category: annotationCategory,
        createdAt: now,
        updatedAt: now
      }
      onAnnotationsChange({ ...currentAnnotations, items: [...currentAnnotations.items, newAnnotation] })
    }
    
    handleCloseAnnotationDialog()
  }

  // Toggle destaque
  const handleToggleHighlight = (segmentIndex: number, color: AnnotationColor = 'yellow') => {
    if (!onAnnotationsChange) return
    
    const segment = transcription.transcript[segmentIndex]
    const currentAnnotations = annotations || { items: [], highlights: [], bookmarks: [] }
    const existingHighlight = currentAnnotations.highlights.find(h => h.segmentIndex === segmentIndex)
    
    if (existingHighlight) {
      // Remover
      onAnnotationsChange({
        ...currentAnnotations,
        highlights: currentAnnotations.highlights.filter(h => h.id !== existingHighlight.id)
      })
    } else {
      // Adicionar
      const newHighlight: SegmentHighlight = {
        id: `high_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        segmentIndex,
        startTime: segment.start_time,
        color,
        createdAt: Date.now()
      }
      onAnnotationsChange({ ...currentAnnotations, highlights: [...currentAnnotations.highlights, newHighlight] })
    }
    handleCloseContextMenu()
  }

  // Toggle favorito
  const handleToggleBookmark = (segmentIndex: number) => {
    if (!onAnnotationsChange) return
    
    const segment = transcription.transcript[segmentIndex]
    const currentAnnotations = annotations || { items: [], highlights: [], bookmarks: [] }
    const existingBookmark = currentAnnotations.bookmarks.find(b => b.segmentIndex === segmentIndex)
    
    if (existingBookmark) {
      // Remover
      onAnnotationsChange({
        ...currentAnnotations,
        bookmarks: currentAnnotations.bookmarks.filter(b => b.id !== existingBookmark.id)
      })
    } else {
      // Adicionar
      const newBookmark: Bookmark = {
        id: `bm_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        segmentIndex,
        startTime: segment.start_time,
        title: `Segmento ${segmentIndex + 1}`,
        description: segment.text.substring(0, 80),
        createdAt: Date.now()
      }
      onAnnotationsChange({ ...currentAnnotations, bookmarks: [...currentAnnotations.bookmarks, newBookmark] })
    }
    handleCloseContextMenu()
  }

  // Deletar anotação
  const handleDeleteAnnotation = (annotationId: string) => {
    if (!onAnnotationsChange || !annotations) return
    onAnnotationsChange({
      ...annotations,
      items: annotations.items.filter(a => a.id !== annotationId)
    })
  }

  // Estado para highlight de segmento navegado externamente
  const [highlightedSegment, setHighlightedSegment] = useState<number | null>(null)

  // Scroll para segmento específico quando solicitado externamente
  useEffect(() => {
    if (scrollToSegmentIndex !== null && scrollToSegmentIndex !== undefined && scrollToSegmentIndex >= 0) {
      // Se o filtro de tempo está ativo, desabilitar para garantir que o segmento está visível
      if (isTimeFilterActive) {
        setIsTimeFilterActive(false)
        setTimeRange([0, totalDuration])
      }
      
      // Aguardar um tick para garantir que a lista foi atualizada
      setTimeout(() => {
        const ref = segmentRefs.current[scrollToSegmentIndex]
        if (ref) {
          ref.scrollIntoView({ behavior: 'smooth', block: 'center' })
          // Destacar o segmento temporariamente
          setHighlightedSegment(scrollToSegmentIndex)
          setTimeout(() => setHighlightedSegment(null), 2000)
        }
      }, 100)
    }
  }, [scrollToSegmentIndex, isTimeFilterActive, totalDuration])

  const handleNextResult = () => {
    if (resultSegmentIndices.length > 0) {
      setCurrentResultIndex((prev) => (prev + 1) % resultSegmentIndices.length)
    }
  }

  const handlePrevResult = () => {
    if (resultSegmentIndices.length > 0) {
      setCurrentResultIndex((prev) => (prev - 1 + resultSegmentIndices.length) % resultSegmentIndices.length)
    }
  }

  const handleClearSearch = () => {
    setSearchQuery('')
    setCurrentResultIndex(0)
  }

  const renderHighlightedText = (text: string, segmentIndex: number) => {
    // Primeiro, aplica highlight de busca
    if (searchQuery.trim()) {
      const parts: React.ReactNode[] = []
      const lowerText = text.toLowerCase()
      const lowerQuery = searchQuery.toLowerCase()
      let lastEnd = 0

      let searchIndex = 0
      while (true) {
        const matchStart = lowerText.indexOf(lowerQuery, searchIndex)
        if (matchStart === -1) break

        // Texto antes do match
        if (matchStart > lastEnd) {
          parts.push(
            <span key={`text-${lastEnd}`}>
              {text.substring(lastEnd, matchStart)}
            </span>
          )
        }

        // Texto do match (highlighted)
        const isCurrentResult = resultSegmentIndices[currentResultIndex] === segmentIndex
        parts.push(
          <mark
            key={`match-${matchStart}`}
            style={{
              backgroundColor: isCurrentResult ? '#ffeb3b' : '#fff59d',
              padding: '0 2px',
              borderRadius: 2
            }}
          >
            {text.substring(matchStart, matchStart + searchQuery.length)}
          </mark>
        )

        lastEnd = matchStart + searchQuery.length
        searchIndex = matchStart + 1
      }

      if (lastEnd < text.length) {
        parts.push(
          <span key={`text-${lastEnd}`}>
            {text.substring(lastEnd)}
          </span>
        )
      }

      return <>{parts}</>
    }

    // Se não há busca, aplica highlight de termos jurídicos
    if (highlightTerms) {
      const parts = highlightLegalTerms(text)
      return (
        <>
          {parts.map((part, i) =>
            part.isHighlight && part.term ? (
              <Tooltip
                key={i}
                title={
                  <Box>
                    <Typography variant="subtitle2">{part.term.term}</Typography>
                    <Typography variant="caption">{part.term.description}</Typography>
                  </Box>
                }
                arrow
              >
                <span
                  style={{
                    backgroundColor: CATEGORY_COLORS[part.term.category]?.bg || '#f5f5f5',
                    color: CATEGORY_COLORS[part.term.category]?.text || '#333',
                    padding: '0 3px',
                    borderRadius: 3,
                    cursor: 'help'
                  }}
                >
                  {part.text}
                </span>
              </Tooltip>
            ) : (
              <span key={i}>{part.text}</span>
            )
          )}
        </>
      )
    }

    return text
  }

  const getSpeakerColor = (speaker: string): string => {
    const colors = ['#1976d2', '#9c27b0', '#2e7d32', '#ed6c02', '#d32f2f', '#0288d1']
    const index = transcription.participants.indexOf(speaker)
    return colors[index >= 0 ? index % colors.length : Math.abs(speaker.charCodeAt(0)) % colors.length]
  }

  return (
    <Box>
      {/* Editor de Speakers */}
      {onSpeakerMappingChange && (
        <SpeakerEditor
          transcription={transcription}
          speakerMapping={speakerMapping}
          onSpeakerMappingChange={onSpeakerMappingChange}
        />
      )}

      {/* Barra de busca e filtros */}
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          bgcolor: '#f8f9fa',
          border: '1px solid #e0e0e0',
          borderRadius: 2,
          position: 'sticky',
          top: 0,
          zIndex: 10,
          overflow: 'hidden'
        }}
      >
        {/* Linha principal: Busca + Botão de Filtros */}
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            placeholder="Buscar na transcrição..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value)
              setCurrentResultIndex(0)
            }}
            size="small"
            sx={{ flex: 1, bgcolor: '#fff' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'action.active' }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          {/* Navegação de resultados de busca */}
          {searchResults.length > 0 && (
            <>
              <Chip
                label={`${currentResultIndex + 1}/${resultSegmentIndices.length}`}
                size="small"
                color="primary"
                variant="outlined"
              />
              <IconButton size="small" onClick={handlePrevResult}>
                <KeyboardArrowUpIcon />
              </IconButton>
              <IconButton size="small" onClick={handleNextResult}>
                <KeyboardArrowDownIcon />
              </IconButton>
            </>
          )}

          {searchQuery && searchResults.length === 0 && (
            <Chip label="Sem resultados" size="small" color="error" variant="outlined" />
          )}
          
          {/* Botão de filtros avançados */}
          <Tooltip title="Filtros avançados">
            <IconButton 
              onClick={() => setFiltersExpanded(!filtersExpanded)}
              sx={{ 
                bgcolor: filtersExpanded || activeFilterCount > 0 ? '#e3f2fd' : 'transparent',
                color: activeFilterCount > 0 ? '#1976d2' : 'inherit'
              }}
            >
              <Badge badgeContent={activeFilterCount} color="primary" max={9}>
                <TuneIcon />
              </Badge>
            </IconButton>
          </Tooltip>
        </Box>
        
        {/* Chips de filtros ativos (resumo) */}
        {hasActiveFilters && !filtersExpanded && (
          <Box sx={{ px: 2, pb: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {isTimeFilterActive && (
              <Chip 
                size="small" 
                label={`⏱️ ${formatSeconds(timeRange[0])} - ${formatSeconds(timeRange[1])}`}
                onDelete={() => { setTimeRange([0, totalDuration]); setIsTimeFilterActive(false) }}
                sx={{ height: 24 }}
              />
            )}
            {annotationFilter !== 'all' && (
              <Chip 
                size="small" 
                icon={annotationFilter === 'bookmarks' ? <BookmarkIcon sx={{ fontSize: 16 }} /> : 
                      annotationFilter === 'notes' ? <EditNoteIcon sx={{ fontSize: 16 }} /> :
                      annotationFilter === 'highlights' ? <HighlightIcon sx={{ fontSize: 16 }} /> :
                      <FilterListIcon sx={{ fontSize: 16 }} />}
                label={annotationFilter === 'bookmarks' ? 'Favoritos' : 
                       annotationFilter === 'notes' ? 'Com notas' :
                       annotationFilter === 'highlights' ? 'Destacados' : 'Com anotação'}
                onDelete={() => setAnnotationFilter('all')}
                sx={{ height: 24 }}
              />
            )}
            {selectedSpeakers.length > 0 && (
              <Chip 
                size="small" 
                icon={<PersonIcon sx={{ fontSize: 16 }} />}
                label={`${selectedSpeakers.length} participante(s)`}
                onDelete={() => setSelectedSpeakers([])}
                sx={{ height: 24 }}
              />
            )}
            {confidenceFilter !== 'all' && (
              <Chip 
                size="small" 
                icon={<WarningAmberIcon sx={{ fontSize: 16 }} />}
                label={confidenceFilter === 'low' ? 'Baixa confiança' : 
                       confidenceFilter === 'medium' ? 'Média confiança' : 'Alta confiança'}
                onDelete={() => setConfidenceFilter('all')}
                sx={{ height: 24 }}
              />
            )}
            {highlightColorFilter !== 'all' && (
              <Chip 
                size="small" 
                sx={{ 
                  height: 24,
                  bgcolor: ANNOTATION_COLORS[highlightColorFilter].bg,
                  borderColor: ANNOTATION_COLORS[highlightColorFilter].border
                }}
                label={`Destaque ${ANNOTATION_COLORS[highlightColorFilter].label}`}
                onDelete={() => setHighlightColorFilter('all')}
              />
            )}
            <Chip 
              size="small" 
              label="Limpar tudo" 
              variant="outlined"
              onClick={handleClearAllFilters}
              sx={{ height: 24 }}
            />
          </Box>
        )}

        {/* Painel de filtros expandido */}
        <Collapse in={filtersExpanded}>
          <Divider />
          <Box sx={{ p: 2, bgcolor: '#fff' }}>
            {/* Filtros rápidos por tipo de anotação */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, display: 'block', mb: 1 }}>
                Filtrar por Anotações
              </Typography>
              <ToggleButtonGroup
                value={annotationFilter}
                exclusive
                onChange={(_, value) => value && setAnnotationFilter(value)}
                size="small"
                sx={{ flexWrap: 'wrap' }}
              >
                <ToggleButton value="all" sx={{ textTransform: 'none' }}>
                  Todos
                </ToggleButton>
                <ToggleButton value="bookmarks" sx={{ textTransform: 'none' }}>
                  <BookmarkIcon sx={{ fontSize: 18, mr: 0.5 }} /> Favoritos
                  {annotations && annotations.bookmarks.length > 0 && (
                    <Chip size="small" label={annotations.bookmarks.length} sx={{ ml: 0.5, height: 18 }} />
                  )}
                </ToggleButton>
                <ToggleButton value="notes" sx={{ textTransform: 'none' }}>
                  <EditNoteIcon sx={{ fontSize: 18, mr: 0.5 }} /> Com notas
                  {annotations && annotations.items.length > 0 && (
                    <Chip size="small" label={annotations.items.length} sx={{ ml: 0.5, height: 18 }} />
                  )}
                </ToggleButton>
                <ToggleButton value="highlights" sx={{ textTransform: 'none' }}>
                  <HighlightIcon sx={{ fontSize: 18, mr: 0.5 }} /> Destaques
                  {annotations && annotations.highlights.length > 0 && (
                    <Chip size="small" label={annotations.highlights.length} sx={{ ml: 0.5, height: 18 }} />
                  )}
                </ToggleButton>
                <ToggleButton value="any-annotation" sx={{ textTransform: 'none' }}>
                  Qualquer anotação
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            
            {/* Filtro por cor de destaque */}
            {annotationFilter === 'highlights' && annotations && annotations.highlights.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, display: 'block', mb: 1 }}>
                  Cor do destaque
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Chip 
                    size="small" 
                    label="Todas" 
                    variant={highlightColorFilter === 'all' ? 'filled' : 'outlined'}
                    onClick={() => setHighlightColorFilter('all')}
                  />
                  {Object.entries(ANNOTATION_COLORS).map(([color, { bg, border, label }]) => (
                    <Chip
                      key={color}
                      size="small"
                      label={label}
                      variant={highlightColorFilter === color ? 'filled' : 'outlined'}
                      onClick={() => setHighlightColorFilter(color as AnnotationColor)}
                      sx={{ 
                        bgcolor: highlightColorFilter === color ? bg : 'transparent',
                        borderColor: border,
                        '&:hover': { bgcolor: bg }
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
            
            {/* Filtro por participante */}
            {transcription.participants.length > 1 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, display: 'block', mb: 1 }}>
                  Filtrar por Participante
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {transcription.participants.map((speaker) => (
                    <Chip
                      key={speaker}
                      size="small"
                      icon={<PersonIcon sx={{ fontSize: 16 }} />}
                      label={getSpeakerName(speaker)}
                      variant={selectedSpeakers.includes(speaker) ? 'filled' : 'outlined'}
                      onClick={() => {
                        if (selectedSpeakers.includes(speaker)) {
                          setSelectedSpeakers(selectedSpeakers.filter(s => s !== speaker))
                        } else {
                          setSelectedSpeakers([...selectedSpeakers, speaker])
                        }
                      }}
                      sx={{ 
                        borderColor: getSpeakerColor(speaker),
                        bgcolor: selectedSpeakers.includes(speaker) ? getSpeakerColor(speaker) : 'transparent',
                        color: selectedSpeakers.includes(speaker) ? '#fff' : 'inherit',
                        '&:hover': { opacity: 0.8 }
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
            
            {/* Filtro por confiança */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, display: 'block', mb: 1 }}>
                Filtrar por Confiança da Transcrição
              </Typography>
              <ToggleButtonGroup
                value={confidenceFilter}
                exclusive
                onChange={(_, value) => value && setConfidenceFilter(value)}
                size="small"
              >
                <ToggleButton value="all" sx={{ textTransform: 'none' }}>
                  Todos
                </ToggleButton>
                <ToggleButton value="low" sx={{ textTransform: 'none', color: '#d32f2f' }}>
                  ⚠️ Baixa (&lt;70%)
                </ToggleButton>
                <ToggleButton value="medium" sx={{ textTransform: 'none', color: '#ed6c02' }}>
                  Média (70-90%)
                </ToggleButton>
                <ToggleButton value="high" sx={{ textTransform: 'none', color: '#2e7d32' }}>
                  ✓ Alta (&gt;90%)
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            
            {/* Slider de tempo */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AccessTimeIcon sx={{ fontSize: 18, color: isTimeFilterActive ? '#1976d2' : '#666' }} />
                <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, flex: 1 }}>
                  Filtrar por Momento
                </Typography>
                <Typography variant="caption" sx={{ color: '#666', fontFamily: 'monospace' }}>
                  {formatSeconds(timeRange[0])} - {formatSeconds(timeRange[1])}
                </Typography>
                {isTimeFilterActive && (
                  <Chip
                    label="Limpar"
                    size="small"
                    onClick={() => {
                      setTimeRange([0, totalDuration])
                      setIsTimeFilterActive(false)
                    }}
                    sx={{ height: 22 }}
                  />
                )}
              </Box>
              <Slider
                value={timeRange}
                onChange={(_, newValue) => {
                  setTimeRange(newValue as [number, number])
                  setIsTimeFilterActive(true)
                }}
                valueLabelDisplay="auto"
                valueLabelFormat={formatSeconds}
                min={0}
                max={totalDuration}
                step={1}
                sx={{
                  color: isTimeFilterActive ? '#1976d2' : '#bdbdbd',
                  '& .MuiSlider-thumb': { width: 16, height: 16 },
                  '& .MuiSlider-valueLabel': { fontFamily: 'monospace', fontSize: 11 }
                }}
              />
            </Box>
            
            {/* Botão limpar todos */}
            {hasActiveFilters && (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button 
                  size="small" 
                  startIcon={<ClearIcon />}
                  onClick={handleClearAllFilters}
                >
                  Limpar todos os filtros
                </Button>
              </Box>
            )}
          </Box>
        </Collapse>
        
        {/* Status de resultados */}
        <Box sx={{ px: 2, py: 1, bgcolor: hasActiveFilters ? '#e3f2fd' : '#f5f5f5', borderTop: '1px solid #e0e0e0' }}>
          <Typography variant="caption" sx={{ color: hasActiveFilters ? '#1976d2' : '#666' }}>
            {hasActiveFilters 
              ? `Exibindo ${filteredTranscript.length} de ${transcription.transcript.length} segmentos`
              : `${transcription.transcript.length} segmentos • ${formatSeconds(totalDuration)} de duração`
            }
          </Typography>
        </Box>
      </Paper>

      {/* Lista de segmentos */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {filteredTranscript.length === 0 ? (
          <Paper elevation={0} sx={{ p: 4, textAlign: 'center', bgcolor: '#f5f5f5' }}>
            <FilterListIcon sx={{ fontSize: 48, color: '#bdbdbd', mb: 1 }} />
            <Typography variant="body1" sx={{ color: '#666', mb: 1 }}>
              Nenhum segmento encontrado
            </Typography>
            <Typography variant="body2" sx={{ color: '#999', mb: 2 }}>
              Tente ajustar os filtros ou limpar a busca
            </Typography>
            <Button variant="outlined" size="small" onClick={handleClearAllFilters}>
              Limpar filtros
            </Button>
          </Paper>
        ) : (
          filteredTranscript.map((segment, index) => {
            // Calcular o índice original para manter referências corretas
            const originalIndex = transcription.transcript.indexOf(segment)
            const isSearchMatch = resultSegmentIndices.includes(originalIndex)
            const isCurrentMatch = resultSegmentIndices[currentResultIndex] === originalIndex
            const isHighlighted = highlightedSegment === originalIndex
            
            // Anotações deste segmento
            const segAnnotations = getSegmentAnnotations(originalIndex)
            const hasHighlight = segAnnotations.highlights.length > 0
            const hasBookmark = segAnnotations.bookmarks.length > 0
            const hasNotes = segAnnotations.notes.length > 0
            const highlightColor = hasHighlight ? segAnnotations.highlights[0].color : null

            return (
              <Paper
                key={originalIndex}
                ref={(el) => (segmentRefs.current[originalIndex] = el)}
                elevation={isCurrentMatch || isHighlighted ? 3 : 0}
                onContextMenu={(e) => handleContextMenu(e, originalIndex)}
              sx={{
                p: 2,
                borderLeft: '4px solid',
                borderLeftColor: hasHighlight && highlightColor
                  ? ANNOTATION_COLORS[highlightColor].border
                  : isHighlighted
                  ? '#1976d2'
                  : isCurrentMatch
                  ? '#ff9800'
                  : isSearchMatch
                  ? '#ffc107'
                  : segment.speaker
                  ? getSpeakerColor(segment.speaker)
                  : '#e0e0e0',
                bgcolor: hasHighlight && highlightColor
                  ? ANNOTATION_COLORS[highlightColor].bg
                  : isHighlighted 
                  ? '#e3f2fd' 
                  : isCurrentMatch 
                  ? '#fff8e1' 
                  : isSearchMatch 
                  ? '#fffde7' 
                  : '#fff',
                transition: 'all 0.3s ease',
                boxShadow: isHighlighted ? '0 0 8px rgba(25, 118, 210, 0.4)' : undefined,
                position: 'relative',
                '&:hover': {
                  bgcolor: hasHighlight && highlightColor 
                    ? ANNOTATION_COLORS[highlightColor].bg 
                    : isHighlighted ? '#e3f2fd' : '#f5f5f5',
                  '& .annotation-actions': {
                    opacity: 1
                  }
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                {/* Timestamp */}
                <Chip
                  label={formatTimestamp(segment.start_time)}
                  size="small"
                  sx={{
                    fontFamily: 'monospace',
                    bgcolor: '#f5f5f5',
                    color: '#666'
                  }}
                />

                {/* Conteúdo */}
                <Box sx={{ flex: 1 }}>
                  {segment.speaker && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <PersonIcon sx={{ fontSize: 16, color: getSpeakerColor(segment.speaker) }} />
                      <Typography
                        variant="subtitle2"
                        sx={{ color: getSpeakerColor(segment.speaker), fontWeight: 600 }}
                      >
                        {getSpeakerName(segment.speaker)}
                      </Typography>
                    </Box>
                  )}
                  <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                    {renderHighlightedText(segment.text, originalIndex)}
                  </Typography>
                  
                  {/* Exibir notas existentes */}
                  {hasNotes && (
                    <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {segAnnotations.notes.map(note => (
                        <Box
                          key={note.id}
                          sx={{
                            p: 1.5,
                            bgcolor: ANNOTATION_COLORS[note.color].bg,
                            borderLeft: `3px solid ${ANNOTATION_COLORS[note.color].border}`,
                            borderRadius: '0 8px 8px 0',
                            fontSize: 13
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                              {ANNOTATION_CATEGORIES[note.category].icon} {ANNOTATION_CATEGORIES[note.category].label}
                            </Typography>
                            {onAnnotationsChange && (
                              <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleOpenAnnotationDialog(originalIndex, note.id)}
                                  sx={{ p: 0.25 }}
                                >
                                  <EditNoteIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleDeleteAnnotation(note.id)}
                                  sx={{ p: 0.25, color: 'error.main' }}
                                >
                                  <DeleteIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Box>
                            )}
                          </Box>
                          <Typography variant="body2">{note.text}</Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>

                {/* Indicadores de anotações */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {/* Indicador de favorito */}
                  {hasBookmark && (
                    <Tooltip title="Favorito">
                      <BookmarkIcon sx={{ fontSize: 20, color: '#f57c00' }} />
                    </Tooltip>
                  )}
                  
                  {/* Indicador de notas */}
                  {hasNotes && (
                    <Tooltip title={`${segAnnotations.notes.length} nota(s)`}>
                      <Badge badgeContent={segAnnotations.notes.length} color="primary" max={9}>
                        <EditNoteIcon sx={{ fontSize: 20, color: '#1976d2' }} />
                      </Badge>
                    </Tooltip>
                  )}
                  
                  {/* Indicador de destaque */}
                  {hasHighlight && (
                    <Tooltip title="Destacado">
                      <HighlightIcon sx={{ fontSize: 20, color: highlightColor ? ANNOTATION_COLORS[highlightColor].border : '#fbc02d' }} />
                    </Tooltip>
                  )}
                </Box>

                {/* Botões de ação (visíveis no hover) */}
                {onAnnotationsChange && (
                  <Box 
                    className="annotation-actions"
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 0.25,
                      opacity: 0,
                      transition: 'opacity 0.2s'
                    }}
                  >
                    <Tooltip title="Adicionar nota">
                      <IconButton 
                        size="small" 
                        onClick={() => handleOpenAnnotationDialog(originalIndex)}
                        sx={{ p: 0.5 }}
                      >
                        <NoteAddIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={hasHighlight ? "Remover destaque" : "Destacar"}>
                      <IconButton 
                        size="small" 
                        onClick={() => handleToggleHighlight(originalIndex)}
                        sx={{ p: 0.5, color: hasHighlight ? '#fbc02d' : undefined }}
                      >
                        <HighlightIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={hasBookmark ? "Remover favorito" : "Favoritar"}>
                      <IconButton 
                        size="small" 
                        onClick={() => handleToggleBookmark(originalIndex)}
                        sx={{ p: 0.5, color: hasBookmark ? '#f57c00' : undefined }}
                      >
                        {hasBookmark ? <BookmarkIcon sx={{ fontSize: 18 }} /> : <BookmarkBorderIcon sx={{ fontSize: 18 }} />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}

                {/* Indicador de confiança */}
                {segment.confidence > 0 && segment.confidence < 0.8 && (
                  <Tooltip title={`Confiança: ${Math.round(segment.confidence * 100)}%`}>
                    <Chip
                      label="⚠️"
                      size="small"
                      sx={{ bgcolor: '#fff3e0', fontSize: 10 }}
                    />
                  </Tooltip>
                )}
              </Box>
            </Paper>
          )
        })
        )}
      </Box>

      {/* Menu de contexto */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={() => contextMenu && handleOpenAnnotationDialog(contextMenu.segmentIndex)}>
          <ListItemIcon><NoteAddIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Adicionar nota</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => contextMenu && handleToggleHighlight(contextMenu.segmentIndex, 'yellow')}>
          <ListItemIcon><HighlightIcon fontSize="small" sx={{ color: ANNOTATION_COLORS.yellow.border }} /></ListItemIcon>
          <ListItemText>Destacar amarelo</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => contextMenu && handleToggleHighlight(contextMenu.segmentIndex, 'green')}>
          <ListItemIcon><HighlightIcon fontSize="small" sx={{ color: ANNOTATION_COLORS.green.border }} /></ListItemIcon>
          <ListItemText>Destacar verde</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => contextMenu && handleToggleHighlight(contextMenu.segmentIndex, 'blue')}>
          <ListItemIcon><HighlightIcon fontSize="small" sx={{ color: ANNOTATION_COLORS.blue.border }} /></ListItemIcon>
          <ListItemText>Destacar azul</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => contextMenu && handleToggleHighlight(contextMenu.segmentIndex, 'red')}>
          <ListItemIcon><HighlightIcon fontSize="small" sx={{ color: ANNOTATION_COLORS.red.border }} /></ListItemIcon>
          <ListItemText>Destacar vermelho</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => contextMenu && handleToggleBookmark(contextMenu.segmentIndex)}>
          <ListItemIcon><BookmarkBorderIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Favoritar</ListItemText>
        </MenuItem>
      </Menu>

      {/* Dialog para adicionar/editar anotação */}
      <Dialog open={annotationDialog?.open || false} onClose={handleCloseAnnotationDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {annotationDialog?.editingId ? 'Editar Nota' : 'Nova Nota'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Nota"
              multiline
              rows={4}
              value={annotationText}
              onChange={(e) => setAnnotationText(e.target.value)}
              fullWidth
              autoFocus
              placeholder="Digite sua anotação..."
            />
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Categoria</InputLabel>
                <Select
                  value={annotationCategory}
                  label="Categoria"
                  onChange={(e) => setAnnotationCategory(e.target.value as AnnotationCategory)}
                >
                  {Object.entries(ANNOTATION_CATEGORIES).map(([key, { icon, label }]) => (
                    <MenuItem key={key} value={key}>
                      {icon} {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <Typography variant="body2" sx={{ mr: 1 }}>Cor:</Typography>
                {Object.entries(ANNOTATION_COLORS).map(([key, { bg, border }]) => (
                  <IconButton
                    key={key}
                    size="small"
                    onClick={() => setAnnotationColor(key as AnnotationColor)}
                    sx={{
                      width: 28,
                      height: 28,
                      bgcolor: bg,
                      border: annotationColor === key ? `2px solid ${border}` : '1px solid #ccc',
                      '&:hover': { bgcolor: bg }
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAnnotationDialog}>Cancelar</Button>
          <Button onClick={handleSaveAnnotation} variant="contained" disabled={!annotationText.trim()}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
